/**
 * UnlinkedPlayers - Manage persons not assigned to any team
 *
 * Features:
 * - Table of persons with no team membership
 * - Search filter (client-side)
 * - Face count filter
 * - Sortable columns
 * - Inline team search per row
 * - Multi-select with bulk team assignment
 * - Row removal animation after assignment
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  fetchPersons,
  searchTeams,
  addTeamMember,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Spinner from '../../../../shared/components/ui/Spinner';
import styles from './UnlinkedPlayers.module.css';

function SortHeader({ label, sortKeyName, sortKey, sortDir, onSort }) {
  return (
    <th
      className={clsx(styles.th, styles.sortable)}
      onClick={() => onSort(sortKeyName)}
    >
      {label}
      {sortKey === sortKeyName && (
        <span className={styles.sortIndicator}>{sortDir === 'asc' ? ' ^' : ' v'}</span>
      )}
    </th>
  );
}

function InlineTeamSearch({ onAssign }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const containerRef = useRef(null);

  // Debounced team search
  useEffect(() => {
    const shouldSkip =
      (selectedTeam && query === selectedTeam.name) ||
      !query ||
      query.length < 2;

    if (shouldSkip) return;

    const timer = setTimeout(async () => {
      try {
        const res = await searchTeams(query);
        setSuggestions(res || []);
      } catch (e) {
        console.error(e);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query, selectedTeam]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectTeam = (team) => {
    setSelectedTeam(team);
    setQuery(team.name);
    setSuggestions([]);
  };

  const clearTeam = () => {
    setSelectedTeam(null);
    setQuery('');
    setSuggestions([]);
  };

  const handleAssign = () => {
    if (selectedTeam) {
      onAssign(selectedTeam.id);
    }
  };

  return (
    <div className={styles.inlineTeamSearch} ref={containerRef}>
      <div className={styles.inlineTeamInputWrapper}>
        <input
          type="text"
          className={styles.inlineTeamInput}
          placeholder="Search teams..."
          value={query}
          onChange={(e) => {
            const val = e.target.value;
            setQuery(val);
            if (selectedTeam) setSelectedTeam(null);
            if (!val || val.length < 2) setSuggestions([]);
          }}
          autoComplete="off"
        />
        {selectedTeam && (
          <button
            type="button"
            className={styles.clearTeamBtn}
            onClick={clearTeam}
            title="Clear team"
          >
            <XMarkIcon className={styles.clearIcon} />
          </button>
        )}
      </div>
      {suggestions.length > 0 && (
        <div className={styles.teamSuggestionsList}>
          {suggestions.map((team) => (
            <button
              key={team.id}
              type="button"
              className={styles.teamSuggestionItem}
              onClick={() => selectTeam(team)}
            >
              <span className={styles.teamName}>{team.name}</span>
              {team.team_type && (
                <span className={styles.teamType}>{team.team_type}</span>
              )}
            </button>
          ))}
        </div>
      )}
      {selectedTeam && (
        <Button variant="primary" size="sm" onClick={handleAssign}>
          Assign
        </Button>
      )}
    </div>
  );
}

function BulkAssignPopover({ onAssign, onClose }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await searchTeams(query);
        setSuggestions(res || []);
      } catch (e) {
        console.error(e);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const handleSelect = async (team) => {
    setAssigning(true);
    try {
      await onAssign(team.id);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className={styles.bulkPopover} ref={containerRef}>
      <input
        type="text"
        className={styles.bulkPopoverInput}
        placeholder="Search team to assign..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
        autoComplete="off"
        disabled={assigning}
      />
      {assigning && (
        <div className={styles.bulkAssigning}>Assigning...</div>
      )}
      {!assigning && suggestions.length > 0 && (
        <div className={styles.bulkSuggestionsList}>
          {suggestions.map((team) => (
            <button
              key={team.id}
              type="button"
              className={styles.teamSuggestionItem}
              onClick={() => handleSelect(team)}
            >
              <span className={styles.teamName}>{team.name}</span>
              {team.team_type && (
                <span className={styles.teamType}>{team.team_type}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UnlinkedPlayers() {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [faceCountFilter, setFaceCountFilter] = useState('all');

  // Sorting
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Selection for bulk assign
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkPopover, setShowBulkPopover] = useState(false);

  // Track rows fading out
  const [fadingIds, setFadingIds] = useState(new Set());

  const loadPersons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPersons({ no_team: 'true', limit: 500 });
      setPersons(data.results || data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPersons();
  }, [loadPersons]);

  // Filter and sort
  const filteredPersons = useMemo(() => {
    let result = persons;

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.display_name?.toLowerCase().includes(term) ||
          p.first_name?.toLowerCase().includes(term) ||
          p.last_name?.toLowerCase().includes(term) ||
          p.jersey_number?.toString().includes(term)
      );
    }

    if (faceCountFilter === 'no_faces') {
      result = result.filter((p) => !p.face_count || p.face_count === 0);
    } else if (faceCountFilter === 'few_faces') {
      result = result.filter((p) => p.face_count > 0 && p.face_count < 10);
    } else if (faceCountFilter === 'many_faces') {
      result = result.filter((p) => p.face_count >= 10);
    }

    result = [...result].sort((a, b) => {
      let aVal;
      let bVal;

      if (sortKey === 'face_count') {
        aVal = a.face_count || 0;
        bVal = b.face_count || 0;
      } else if (sortKey === 'created_at') {
        aVal = a.created_at || '';
        bVal = b.created_at || '';
      } else {
        const rawA = a[sortKey] || '';
        const rawB = b[sortKey] || '';
        aVal = typeof rawA === 'string' ? rawA.toLowerCase() : rawA;
        bVal = typeof rawB === 'string' ? rawB.toLowerCase() : rawB;
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [persons, search, faceCountFilter, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredPersons.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPersons.map((p) => p.id)));
    }
  };

  const removePersons = (ids) => {
    const idSet = new Set(ids);
    setFadingIds((prev) => new Set([...prev, ...idSet]));
    setTimeout(() => {
      setPersons((prev) => prev.filter((p) => !idSet.has(p.id)));
      setFadingIds((prev) => {
        const next = new Set(prev);
        for (const id of idSet) next.delete(id);
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of idSet) next.delete(id);
        return next;
      });
    }, 300);
  };

  const handleInlineAssign = async (personId, teamId) => {
    try {
      const person = persons.find((p) => p.id === personId);
      const jerseyNum = person?.jersey_number || null;
      await addTeamMember(teamId, personId, jerseyNum);
      removePersons([personId]);
    } catch (err) {
      console.error('Failed to assign team:', err);
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleBulkAssign = async (teamId) => {
    const ids = [...selectedIds];
    const errors = [];
    for (const personId of ids) {
      try {
        const person = persons.find((p) => p.id === personId);
        const jerseyNum = person?.jersey_number || null;
        await addTeamMember(teamId, personId, jerseyNum);
      } catch (err) {
        errors.push(personId);
        console.error(`Failed to assign person ${personId}:`, err);
      }
    }
    const successIds = ids.filter((id) => !errors.includes(id));
    if (successIds.length > 0) {
      removePersons(successIds);
    }
    if (errors.length > 0) {
      setError(`Failed to assign ${errors.length} person(s). Please retry.`);
    }
    setShowBulkPopover(false);
  };

  if (loading && persons.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading unlinked persons...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Unlinked Players</h2>
          <p className={styles.subtitle}>
            Persons not assigned to any team
          </p>
        </div>
        <div className={styles.headerActions}>
          {selectedIds.size > 0 && (
            <>
              <Button variant="ghost" onClick={() => setSelectedIds(new Set())}>
                Clear ({selectedIds.size})
              </Button>
              <div className={styles.bulkAssignWrapper}>
                <Button
                  variant="primary"
                  onClick={() => setShowBulkPopover((v) => !v)}
                >
                  Assign Selected ({selectedIds.size})
                </Button>
                {showBulkPopover && (
                  <BulkAssignPopover
                    onAssign={handleBulkAssign}
                    onClose={() => setShowBulkPopover(false)}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search persons..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className={styles.filterSelect}
          value={faceCountFilter}
          onChange={(e) => setFaceCountFilter(e.target.value)}
        >
          <option value="all">All Face Counts</option>
          <option value="no_faces">No Faces</option>
          <option value="few_faces">1-9 Faces</option>
          <option value="many_faces">10+ Faces</option>
        </select>

        <span className={styles.count}>{filteredPersons.length} unlinked persons</span>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>
                <input
                  type="checkbox"
                  checked={filteredPersons.length > 0 && selectedIds.size === filteredPersons.length}
                  onChange={toggleSelectAll}
                  className={styles.checkbox}
                />
              </th>
              <SortHeader label="Display Name" sortKeyName="display_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="First Name" sortKeyName="first_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Last Name" sortKeyName="last_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Jersey" sortKeyName="jersey_number" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Faces" sortKeyName="face_count" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className={styles.th}>Assign Team</th>
            </tr>
          </thead>
          <tbody>
            {filteredPersons.map((person) => (
              <tr
                key={person.id}
                className={clsx(
                  styles.row,
                  selectedIds.has(person.id) && styles.selected,
                  fadingIds.has(person.id) && styles.fading,
                )}
              >
                <td className={styles.td}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(person.id)}
                    onChange={() => toggleSelect(person.id)}
                    className={styles.checkbox}
                  />
                </td>
                <td className={styles.td}>
                  <span className={styles.personName}>{person.display_name}</span>
                </td>
                <td className={styles.td}>
                  <span className={styles.dimText}>{person.first_name || '-'}</span>
                </td>
                <td className={styles.td}>
                  <span className={styles.dimText}>{person.last_name || '-'}</span>
                </td>
                <td className={styles.td}>{person.jersey_number || '-'}</td>
                <td className={styles.td}>
                  <span className={styles.faceCount}>{person.face_count || 0}</span>
                </td>
                <td className={styles.td}>
                  <InlineTeamSearch
                    onAssign={(teamId) => handleInlineAssign(person.id, teamId)}
                  />
                </td>
              </tr>
            ))}
            {filteredPersons.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  All persons are assigned to teams.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
